/**
 * CONTRACT.md §2.11 — the stepper, and the state word that stands alone.
 *
 * `Step` has two jobs and one class. Inside a `Stepper` it is one stage of the
 * work-order lifecycle, and `Stepper` draws the `›` separators itself so a page
 * never writes one. Outside a `Stepper` it is §5's answer to the refused
 * `.state`: "the one-word 'where it is' value in a data cell
 * (`<span class="step step--now">proposed</span>`) … A `.pill` is wrong there,
 * because the column header already frames the state."
 *
 * `step--now` is emphasised by **weight**, not colour (§3: it was ochre in v1
 * and is not any more).
 */

import { Children, Fragment, type ReactNode } from 'react'

import { cx, type Common } from './common.js'

export type StepperProps = Common & { readonly children: ReactNode }

/**
 * The separators are interleaved here rather than written by the page. It is the
 * one place in this folder that reads its children, and the reason is that a
 * page writing `<span class="step__sep">›</span>` six times is a page writing
 * markup — and getting the last one wrong.
 *
 * The wrapper is a `Fragment`, not an element: `.stepper` is the flex container
 * and every `.step` and `.step__sep` has to be a direct child of it.
 */
export function Stepper({ children, ...common }: StepperProps) {
  const steps = Children.toArray(children)
  return (
    <div className="stepper" {...common}>
      {steps.map((step, index) => (
        <Fragment key={index}>
          {index === 0 ? null : (
            <span className="step__sep" aria-hidden="true">
              {'›'}
            </span>
          )}
          {step}
        </Fragment>
      ))}
    </div>
  )
}

/** `done` · `now` · neither, which is "not yet". */
export type StepState = 'done' | 'now'

export type StepProps = Common & {
  readonly state?: StepState | undefined
  readonly children: ReactNode
}

export function Step({ state, children, ...common }: StepProps) {
  return (
    <span
      className={cx('step', state === 'done' && 'step--done', state === 'now' && 'step--now')}
      aria-current={state === 'now' ? 'step' : undefined}
      {...common}
    >
      {children}
    </span>
  )
}
